import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, MessageCircle, X, Send, Loader2, Clock } from "lucide-react";
import { finApi, apiErr } from "../api";
import { useFin } from "../FinanceApp";
import { SUPPORT_WHATSAPP } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const HIDE = "fin_zatca_banner_hidden";

const RequestDialog = ({ open, onOpenChange, onSent }) => {
  const { user } = useFin();
  const [f, setF] = useState({ contact_name: user?.name || "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await finApi.zatcaCreateLinkRequest(f); toast.success("تم إرسال طلب الربط — سيتواصل معك الدعم الفني قريباً"); onSent(r); onOpenChange(false); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body sm:max-w-md">
        <DialogHeader className="text-start"><DialogTitle>طلب ربط مع هيئة الزكاة (ZATCA)</DialogTitle>
          <DialogDescription>يصل طلبك مباشرة للدعم الفني، ونتواصل معك لإتمام الربط خطوة بخطوة.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>اسم المسؤول</Label><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid="fin-zreq-name" /></div>
          <div className="space-y-1.5"><Label>رقم الجوال</Label><Input dir="ltr" inputMode="tel" placeholder="05xxxxxxxx" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="fin-zreq-phone" /></div>
          <div className="space-y-1.5"><Label>ملاحظات (اختياري)</Label><Textarea rows={3} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="مثال: لدينا حساب في بوابة فاتورة ونحتاج المساعدة في إصدار OTP" data-testid="fin-zreq-note" /></div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button onClick={send} disabled={busy} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="fin-zreq-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال الطلب</Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export function ZatcaBanner() {
  const { user } = useFin();
  const [show, setShow] = useState(false);
  const [req, setReq] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem(HIDE)) return;
    finApi.zatcaStatus().then((s) => {
      const live = s?.pki?.status === "active" && s?.pki?.environment === "production";
      setShow(!live);
      if (!live) finApi.zatcaLinkRequest().then(setReq).catch(() => {});
    }).catch(() => {});
  }, []);
  if (!show) return null;
  const pending = req && ["new", "in_progress"].includes(req.status);
  const msg = `مرحباً، أرغب بربط شركتي (${user?.company?.name || ""}) مع هيئة الزكاة والضريبة والجمارك (الفوترة الإلكترونية - المرحلة الثانية) في برنامج WorkZone المحاسبي.`;
  const hide = () => { sessionStorage.setItem(HIDE, "1"); setShow(false); };
  return (
    <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-gradient-to-l from-emerald-50 to-white p-4 sm:flex-row sm:items-center" data-testid="fin-zatca-banner">
      <div className="flex flex-1 items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-slate-950"><ShieldCheck className="h-5 w-5" /></div>
        <div>
          <div className="font-extrabold text-slate-900">اربط شركتك مع هيئة الزكاة والضريبة (فاتورة - المرحلة الثانية)</div>
          {pending
            ? <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-amber-700" data-testid="fin-zatca-banner-pending"><Clock className="h-3.5 w-3.5" /> تم استلام طلب الربط ({req.status === "new" ? "جديد" : "قيد التنفيذ"}) — سيتواصل معك الدعم الفني على {req.phone}</p>
            : <p className="mt-0.5 text-xs leading-relaxed text-slate-600">الربط إلزامي لإصدار الفواتير الإلكترونية. أرسل طلب ربط أو تواصل مع الدعم الفني وسنربط شركتك مع منصة فاتورة خطوة بخطوة.</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!pending && <Button onClick={() => setOpen(true)} className="flex-1 gap-2 rounded-xl bg-slate-900 hover:bg-slate-800 sm:flex-none" data-testid="fin-zatca-banner-request"><Send className="h-4 w-4" /> إرسال طلب ربط</Button>}
        <a href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid="fin-zatca-banner-support"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 sm:flex-none">
          <MessageCircle className="h-4 w-4" /> واتساب الدعم
        </a>
        <button onClick={hide} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100" aria-label="إخفاء" data-testid="fin-zatca-banner-close"><X className="h-4 w-4" /></button>
      </div>
      <RequestDialog open={open} onOpenChange={setOpen} onSent={setReq} />
    </div>
  );
}
